/**
 * The languages the counter speaks, and how one is chosen (GAP-05, ADR-0037).
 *
 * Three layers decide it, narrowest last (staff > device > business):
 *
 *   1. The business default — `pos.locale`, the tenant setting `/pos/context` already serves as
 *      `Context.locale`. It was required and unread until now (the counter refuses to open without
 *      it, then rendered English regardless).
 *   2. This till — a device choice for whoever signs in here without a choice of their own.
 *   3. The person at the till — `preferredLocale` on their staff profile (GET/PUT /pos/me/preferences),
 *      cached per staff id on this device so it applies before the edge answers (`staff-locale.ts`).
 *      It follows them to another till. A shared till serves a Hindi-speaking cashier on the
 *      morning shift and an English-speaking manager at close; neither changes the other's screen.
 *
 * Anything this app cannot render (a `ta-IN` written into settings ahead of its catalog) falls back
 * to English rather than to a blank key.
 */
export const LOCALES = ["en-IN", "hi-IN"] as const;
export type CounterLocale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: CounterLocale = "en-IN";

/** Accepts what a settings row or a browser might say ("hi", "hi_IN", "HI-in") and narrows it. */
export function normaliseLocale(tag: string | null | undefined): CounterLocale | undefined {
  if (!tag) return undefined;
  const language = tag.trim().replace("_", "-").split("-")[0]?.toLowerCase();
  if (language === "hi") return "hi-IN";
  if (language === "en") return "en-IN";
  return undefined;
}

/**
 * The person's own choice (their staff profile) wins over this till's choice, which wins over the
 * business default; all fall back to English.
 *
 * Called with two arguments (staff, business) it keeps its original meaning.
 */
export function resolveLocale(
  staffChoice: string | null | undefined,
  businessDefault: string | null | undefined,
  deviceChoice?: string | null,
): CounterLocale {
  return normaliseLocale(staffChoice) ?? normaliseLocale(deviceChoice) ?? normaliseLocale(businessDefault) ?? DEFAULT_LOCALE;
}

/**
 * Each language's short mark for the quick switch, in its own script — a cashier who cannot read
 * the other language still recognises their own. Not translated, so not in the catalogs.
 */
export const LOCALE_SHORT: Record<CounterLocale, string> = { "en-IN": "EN", "hi-IN": "हि" };

/**
 * The BCP 47 tag handed to `Intl` for numbers, money and dates.
 *
 * Hindi keeps Latin digits (0–9), pinned with the `nu-latn` extension rather than trusted to the
 * runtime's default: Indian retail bills, GST invoices and UPI amounts are printed in Latin digits,
 * and a cashier reading "₹१,२३,४५६.५०" against the customer's UPI screen is a mismatch waiting to
 * happen. Indian grouping (1,23,456.50) comes from the `IN` region in both locales.
 */
export function intlLocale(locale: CounterLocale): string {
  return locale === "hi-IN" ? "hi-IN-u-nu-latn" : "en-IN";
}

/** The `lang` attribute for the document. English keeps the `en` the layout has always rendered. */
export function htmlLang(locale: CounterLocale): string {
  return locale === "hi-IN" ? "hi-IN" : "en";
}
